import React, { useState, useEffect } from 'react';
import { Send, Person, AccessTime } from '@mui/icons-material';

const CommentsSection = ({ loanId, clientId }) => {
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const fetchComments = async () => {
        if (!loanId) return;

        setLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_HOST_COMMENTS}/query`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        query: {
                            entityId: loanId,
                            entityType: 'loan',
                            paging: {
                                query: {
                                    limit: 50,
                                    page: 1
                                }
                            }
                        }
                    }),
                }
            );
            const data = await response.json();
            if (data.comments) {
                setComments(data.comments);
            }
        } catch (error) {
            console.error('Error fetching comments:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComments();
    }, [loanId]);

    const commentsEndRef = React.useRef(null);

    const scrollToBottom = () => {
        commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (comments.some(c => c.isNew)) {
            scrollToBottom();
        }
    }, [comments]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;

        setSubmitting(true);
        try {
            const payload = {
                message: newComment,
                entityId: loanId,
                entityType: 'loan',
                clientId: clientId
            };

            const response = await fetch(
                `${process.env.REACT_APP_API_HOST_COMMENTS}/create`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        payload,
                        clientId: clientId,
                        loanId: loanId
                    }),
                }
            );

            if (response.ok) {
                // Optimistic update
                const optimisticComment = {
                    ...payload,
                    createdAt: new Date().toISOString(),
                    userName: 'You', // Placeholder since we don't have user info in props
                    isNew: true,
                    _id: Date.now().toString() // Temporary ID
                };

                setComments(prev => [...prev, optimisticComment]);
                setNewComment('');
                // Do not fetchComments() to preserve the "bottom" position for this session
            }
        } catch (error) {
            console.error('Error posting comment:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        } catch (e) {
            return dateString;
        }
    };

    return (
        <div className="bg-white rounded-lg border border-gray-200 flex flex-col h-full">
            <div className="p-4 border-b bg-gray-50">
                <h3 className="text-lg font-bold text-gray-900">Comments</h3>
            </div>

            {/* Comments List */}
            <div className="flex-grow overflow-y-auto p-4 space-y-4 max-h-[400px]">
                {loading ? (
                    <div className="text-center text-gray-500 py-4">Loading comments...</div>
                ) : comments.length === 0 ? (
                    <div className="text-center text-gray-500 py-4">No comments yet.</div>
                ) : (
                    comments.map((comment, index) => (
                        <div
                            key={comment._id || index}
                            className={`rounded-lg p-3 ${comment.isNew ? 'bg-yellow-50 border border-yellow-200 shadow-sm' : 'bg-gray-50'}`}
                        >
                            <div className="flex justify-between items-start mb-1">
                                <span className="font-semibold text-sm text-blue-900 flex items-center gap-1">
                                    <Person fontSize="small" />
                                    {comment.userName || 'Unknown User'}
                                    {comment.isNew && <span className="text-xs text-yellow-600 bg-yellow-100 px-1.5 py-0.5 rounded-full">Just now</span>}
                                </span>
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                    <AccessTime fontSize="small" style={{ fontSize: '12px' }} />
                                    {formatDate(comment.createdAt)}
                                </span>
                            </div>
                            <p className="text-gray-700 text-sm whitespace-pre-wrap">{comment.message}</p>
                        </div>
                    ))
                )}
                <div ref={commentsEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 border-t bg-gray-50">
                <form onSubmit={handleSubmit} className="flex gap-2">
                    <input
                        type="text"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment..."
                        className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={submitting}
                    />
                    <button
                        type="submit"
                        disabled={submitting || !newComment.trim()}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <Send fontSize="small" />
                        {submitting ? 'Sending...' : 'Send'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CommentsSection;
